package com.pincer.core

import com.pincer.core.multiplatform.mpLog
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.time.Clock

/*
Logging in a Pincer context is simple and colourful.
Client and server side share the same code and if timestamping is on you could concatenate logging from
both sides and sort to see the full story of the interactions.
Loggers are set up at a high level and then passed down through the stack.
The device, principal and pid, if they are available, are all used to group the related activity.
Grab a Logger by initialising with these, or call the logger() function on a Logger you have been
given in order to be more specific about the context.

I've looked at different options for tackling the ugly business of slf4j/logback/util.logging etc.
For example, javalin uses slf4j so in those modules we declare that dependency (and simple logging to route it to std.err)
Not hard to have jvmMain in core declare those same dependencies to route it all the same way...
   private val slf4j:org.slf4j.Logger = org.slf4j.LoggerFactory.getLogger("slf")
   actual fun mpLog(line: String) = slf4j.info(line)
But that doesn't get you much.
Another way would be to do whatever simplelogger does on the classpath to be the slf implementation (but that would be upside down?)
But slf4j really wants you to live its api to save yourself from the mad bad world of java logging.
The problem is our multiplatform ambitions take all that to the next level.

I also looked at one multiplatform library: kermit. But it ended up adding nothing.
There's others I haven't looked at (https://github.com/LighthouseGames/KmLogging).

Maybe the next step in this journey would be to beef up our multiplatform logging interface.
Instead of a single string method, it would send down a richer signal of at least log level.
Inside the jvm implementation this would just map through to slf4j (and then on to whatever).
The device.principal.pid stuff would stay in core, but colours and such would go down the stack.
*/

@Serializable
internal data class LogLine(val lvl: String, val dev: String, val pri: String, val pid: String, val msg: String, val err: String? = null) {

    fun json(): String = Json.encodeToString(serializer(), this)

}

// TODO Maybe we change the top three to var ... often we get more information about a request
// as we work through it and we end up with a chain of log > plog > pplog which could instead
// stay the same logger object, but with the extra data as it becomes available.

class Logger(
    val    deviceId: String = "-".repeat(12),
    val principalId: String = "-".repeat(12),
    val         pid: String = "-".repeat(12),
    val     oneLine: Boolean = false,
    val        ansi: Boolean = false,
    val        tags: Boolean = false,
    val   timestamp: Boolean = false,
    val        json: Boolean = false,
    val      indent: Int = 0,
    val        name: String = "",
) {

    fun nameAppend(append: String?): String {
        if (append == null || append == "") return name
        if (name == "") return append
        return "$name.$append"
    }

    fun logger(
            withDeviceId: String? = null,
            withPrincipalId: String? = null,
            withPid:String? = null,
            withIndent: Int? = null,
            withName: String? = null
        )
        = Logger(
            deviceId = withDeviceId?.substring(4) ?: deviceId,
            principalId = withPrincipalId?.substring(4) ?: principalId,
            pid = withPid ?: pid,
            oneLine = oneLine,
            ansi = ansi,
            tags = tags,
            timestamp = timestamp,
            json = json,
            indent = withIndent ?: indent,
            name = nameAppend(withName),
        )

    private val ANSI_RESET   = "\u001B[0m"
    private val ANSI_RED     = "\u001B[31m"
    private val ANSI_GREEN   = "\u001B[32m"
    private val ANSI_YELLOW  = "\u001B[33m"
    private val ANSI_BLUE    = "\u001B[34m"
    private val ANSI_MAGENTA = "\u001B[35m"
    private val ANSI_CYAN    = "\u001B[36m"

    private val indentString = "  ".repeat(indent)

    internal fun doFormat(level: String, message: String, throwable: Throwable?): String {
        if (json) return LogLine(level, deviceId, principalId, pid, message, throwable?.stackTraceToString()).json()
        var build = StringBuilder()
        if (ansi) {
            if (deviceId.equals("ROSENCRANTZZ")) build.append(ANSI_GREEN)
            if (deviceId.equals("RAGOZZZZZINE")) build.append(ANSI_MAGENTA)
            if (level == "FINE") build.append(ANSI_CYAN)
            if (level == "WARN") build.append(ANSI_YELLOW)
            if (level == "ERRR") build.append(ANSI_RED)
            if (level == "HIGH") build.append(ANSI_BLUE)
        }
              if (timestamp) build.append("${Clock.System.now().toString().substring(0,23)}  ")
                             build.append("$level ")
                   if (tags) build.append("($deviceId.$principalId.$pid) ")
             if (name != "") build.append("$name ")
                             build.append("$message ")
        if (throwable!=null) build.append(throwable.stackTraceToString())
                   if (ansi) build.append(ANSI_RESET)

        if (oneLine) {
            return build.toString().replace("\n", "\\r") // use r rather than n for cloudwatch
        }

        return build.toString()
    }

    fun f(message: String, throwable: Throwable? = null) = mpLog(doFormat("FINE", message, throwable))
    fun i(message: String, throwable: Throwable? = null) = mpLog(doFormat("INFO", message, throwable))
    fun w(message: String, throwable: Throwable? = null) = mpLog(doFormat("WARN", message, throwable))
    fun e(message: String, throwable: Throwable? = null) = mpLog(doFormat("ERRR", message, throwable))
    fun h(message: String, throwable: Throwable? = null) = mpLog(doFormat("HIGH", message, throwable))

}